import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { buildDocModel, modelToMarkdown } from "@/lib/docModel";
import { downloadPdf } from "@/lib/pdf";
import DocumentView from "@/components/DocumentView";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { CheckCircle2, MessageSquareWarning, Download, Loader2, ShieldCheck } from "lucide-react";

export default function ReviewPortal() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [finalStatus, setFinalStatus] = useState(null);

  useEffect(() => {
    api.get(`/review/${slug}`)
      .then((r) => { setData(r.data); setFinalStatus(r.data.review?.status); setFeedback(r.data.review?.feedback || ""); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const model = useMemo(() => {
    if (!data) return null;
    return buildDocModel(data.doc_type, { name: data.project_name }, data.phase_data);
  }, [data]);

  const brandColor = data?.branding?.brand_color || "#10B981";
  const agencyName = data?.branding?.agency_name || "Performance Flow Pro";
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    if (!model) return;
    setDownloading(true);
    try { await downloadPdf({ model, brandColor, agencyName, filename: model.title }); }
    catch (e) { toast.error("PDF download failed"); }
    finally { setDownloading(false); }
  };

  const approve = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/review/${slug}/approve`);
      setFinalStatus(res.data.status);
      toast.success("Document approved — thank you!");
    } catch (e) { toast.error("Could not approve"); }
    finally { setSubmitting(false); }
  };

  const submitChanges = async () => {
    if (!feedback.trim()) { toast.error("Please enter your feedback"); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/review/${slug}/request-changes`, { feedback });
      setFinalStatus(res.data.status);
      setShowFeedback(false);
      toast.success("Feedback submitted to the consultant");
    } catch (e) { toast.error("Could not submit feedback"); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }
  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100 text-center px-6">
        <h1 className="font-display text-2xl font-bold text-slate-900">Review link not found</h1>
        <p className="text-slate-500 mt-2">This link may have expired or is invalid.</p>
      </div>
    );
  }

  const decided = finalStatus === "Approved" || finalStatus === "Revision Needed";

  return (
    <div className="min-h-screen bg-slate-100 pb-32">
      <Toaster position="top-center" richColors />
      <header className="border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md flex items-center justify-center" style={{ background: brandColor }}>
              <ShieldCheck className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 leading-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{agencyName}</p>
              <p className="text-[11px] text-slate-400 leading-tight">Client Review Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={finalStatus} />
            <button data-testid="portal-download-pdf" onClick={handleDownloadPdf} disabled={downloading} className="no-print flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-60">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
            </button>
          </div>
        </div>
      </header>

      <main className="py-8 px-4 flex justify-center">
        {model && <DocumentView model={model} brandColor={brandColor} agencyName={agencyName} />}
      </main>

      {/* Floating sticky action bar */}
      <div className="no-print fixed bottom-0 inset-x-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur-xl shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {decided ? (
            <div data-testid="decision-banner" className={`flex items-center justify-center gap-2 font-medium ${finalStatus === "Approved" ? "text-emerald-600" : "text-amber-600"}`}>
              {finalStatus === "Approved" ? <CheckCircle2 className="h-5 w-5" /> : <MessageSquareWarning className="h-5 w-5" />}
              {finalStatus === "Approved" ? "You approved this document. Thank you!" : "Change request submitted. The consultant has been notified."}
            </div>
          ) : showFeedback ? (
            <div className="space-y-3">
              <textarea
                data-testid="feedback-textarea"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={3}
                placeholder="Describe the changes you'd like to see…"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 outline-none resize-none"
              />
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setShowFeedback(false)} className="px-4 py-2 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
                <button data-testid="submit-feedback-btn" onClick={submitChanges} disabled={submitting} className="px-5 py-2 rounded-md text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-60 flex items-center gap-2">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Submit Feedback
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-3">
              <button
                data-testid="approve-btn"
                onClick={approve}
                disabled={submitting}
                className="flex items-center gap-2 px-8 py-3 rounded-md font-semibold text-white bg-emerald-500 hover:bg-emerald-600 shadow-sm transition-colors disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} Approve Document
              </button>
              <button
                data-testid="request-changes-btn"
                onClick={() => setShowFeedback(true)}
                className="flex items-center gap-2 px-6 py-3 rounded-md font-semibold text-white bg-amber-500 hover:bg-amber-600 shadow-sm transition-colors"
              >
                <MessageSquareWarning className="h-5 w-5" /> Request Changes
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
